import { useEffect, useState } from "react";
import axios from "axios";
import SEO from "../components/SEO";
import ProfileHero from "../components/profile/ProfileHero";
import ProfileAbout from "../components/profile/ProfileAbout";
import ProfileExpertise from "../components/profile/ProfileExpertise";
import ProfileApproach from "../components/profile/ProfileApproach";
import ProfileJourney from "../components/profile/ProfileJourney";
import ProfileCerts from "../components/profile/ProfileCerts";
import ProfileShowcase from "../components/profile/ProfileShowcase";
import ProfileLinkedin from "../components/profile/ProfileLinkedin";
import { FORMATIONS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://novalean-consulting.fr";
const PHOTO = "/images/majdi-zarougui.jpg";

function buildJsonLd(profile, books, tools) {
  const org = { "@type": "Organization", name: "NovaLean Consulting", url: SITE };
  const person = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: profile.name,
    jobTitle: profile.job_title,
    description: profile.seo_description,
    image: `${SITE}${PHOTO}`,
    url: `${SITE}/majdi-zarougui`,
    knowsAbout: (profile.expertise_groups || []).flatMap((g) => [g.title, ...(g.items || [])]).slice(0, 40),
    worksFor: org,
  };
  if (profile.linkedin_url) person.sameAs = [profile.linkedin_url];
  const schemas = [person, {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "NovaLean Consulting",
    url: SITE,
    founder: { "@type": "Person", name: profile.name, url: `${SITE}/majdi-zarougui` },
  }];
  books.forEach((b) => {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Book",
      name: b.title,
      author: { "@type": "Person", name: b.author || profile.name, url: `${SITE}/majdi-zarougui` },
      url: b.buy_url || `${SITE}/livres`,
      ...(b.has_cover ? { image: `${API}/books/${b.id}/cover` } : {}),
      ...(b.description ? { description: b.description.slice(0, 300) } : {}),
    });
  });
  FORMATIONS.forEach((f) => {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Course",
      name: `Formation ${f.title}`,
      description: `Formation ${f.tag} — niveau ${f.level}, ${f.duration}, animée par ${profile.name}.`,
      provider: org,
      offers: { "@type": "Offer", price: f.price, priceCurrency: "EUR" },
    });
  });
  tools.forEach((t) => {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: t.name,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: `${SITE}/outils-apps/${t.slug}`,
      author: { "@type": "Person", name: profile.name },
      ...(t.price > 0 && !t.free ? { offers: { "@type": "Offer", price: t.price, priceCurrency: "EUR" } } : {}),
    });
  });
  return schemas;
}

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [books, setBooks] = useState([]);
  const [tools, setTools] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: b }, { data: t }] = await Promise.all([
          axios.get(`${API}/profile`),
          axios.get(`${API}/books`),
          axios.get(`${API}/tools`),
        ]);
        setProfile(p);
        setBooks(b || []);
        setTools(t || []);
      } catch (e) {
        setError(true);
      }
    })();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-6" data-testid="profile-error">
        <p className="text-ink/60 text-lg">Le profil est momentanément indisponible. Réessayez dans un instant.</p>
      </div>
    );
  }
  if (!profile) {
    return <div className="min-h-screen bg-ink-950" data-testid="profile-loading" />;
  }

  const photoUrl = profile.has_photo ? `${API}/profile/photo` : PHOTO;
  return (
    <>
      <SEO title={profile.seo_title} description={profile.seo_description}
        path="/majdi-zarougui" image={PHOTO} jsonLd={buildJsonLd(profile, books, tools)} />
      <ProfileHero profile={profile} photoUrl={photoUrl} />
      <ProfileAbout profile={profile} />
      <ProfileExpertise groups={profile.expertise_groups} />
      <ProfileApproach steps={profile.approach} />
      <ProfileJourney experiences={profile.experiences} />
      <ProfileCerts certifications={profile.certifications} />
      <ProfileShowcase books={books} tools={tools} formations={FORMATIONS} />
      <ProfileLinkedin profile={profile} />
    </>
  );
}
